import React, {ReactNode} from "react";
import {Changelog, EvalData, FilterDef} from "../model";
import {TableView} from "./table";
import {Tooltip} from "react-tooltip";
import {copyTextToClipboard} from "../clipboard";
import {BenchmarkVariant, splitVariants} from "../variants";
import {ExamplesView} from "./examples";

interface AppViewState {
    variants: BenchmarkVariant[] | null;
    activeTab: number;
    data: EvalData | null;
    metric: string | null;
    filters: { [key: string]: string };
    filterSet: Set<string>;
    changelog: Changelog[];
}

export class AppView extends React.Component<any, AppViewState> {
    constructor(props: any) {
        super(props);
        this.state = {
            variants: null,
            activeTab: 0,
            data: null,
            metric: null,
            filters: {},
            filterSet: new Set(),
            changelog: []
        };
    }

    componentDidMount() {
        const url = `${window.location.protocol}//${window.location.host}/data.json`;
        fetch(url)
            .then(val => val.json())
            .then(val => {
                this.setState({changelog: this.changelog(val)});
                if (val.variants && val.variants.length > 0) {
                    const variants = splitVariants(val);
                    this.setState({variants: variants, activeTab: 0});
                    this.applyData(variants[0].data);
                } else {
                    this.applyData(val);
                }
            });
    }

    private selectVariant(index: number) {
        const variants = this.state.variants;
        if (!variants || index < 0 || index >= variants.length || index === this.state.activeTab) return;
        this.setState({activeTab: index});
        this.applyData(variants[index].data);
    }

    private applyData(data: EvalData) {
        this.setState({
            data: data,
            metric: null,
            filters: {},
            filterSet: this.filters(data)
        });
    }

    private filters(data: EvalData) {
        const filters = data?.filters || [];
        const filterSet = new Set<string>();
        for (const filter of filters) {
            const filterOptions = filter.options;
            if (filterOptions.length > 0) {
                filterSet.add(filterOptions[0].tag)
            }
        }
        return filterSet;
    }

    private changelog(data: EvalData) {
        let modelNames: any = {};
        for (const model of (data?.models || [])) {
            modelNames[model.id] = model.name;
        }
        const changelog: Changelog[] = (data?.results || []).map(val => {
            const updated = val.timestamp.substring(0, val.timestamp.indexOf(","));
            const parts = updated.split("/");
            const timestamp = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
            return {
                model: modelNames[val.id] || val.id,
                updated: updated,
                timestamp: timestamp
            };
        });
        changelog.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        return changelog;
    }

    private renderMetrics(): ReactNode {
        const metrics = this.state.data?.metrics || [];
        const selected = this.state.metric || metrics[0].id;
        const options = [];
        for (const metric of metrics) {
            options.push(
                <input type="radio" id={metric.id} name="metric" key={metric.id}
                       value={metric.id} checked={metric.id === selected}
                       onChange={(event) => this.selectMetric(event)}>
                </input>
            );
            options.push(<label htmlFor={metric.id} key={"label " + metric.id}>{metric.name || metric.id}</label>)
        }
        return (
            <div className="filter-block" key="metric">
                <strong>Evaluation metric:</strong>
                {options}
            </div>
        )
    }

    private renderFilters(): ReactNode[] {
        const filters = this.state.data?.filters || [];
        return filters.map(val => this.renderFiltersBlock(val));
    }

    private renderFiltersBlock(spec: FilterDef): ReactNode {
        const filterOptions = spec.options;
        const selected = this.state.filters[spec.name] || filterOptions[0].tag;
        const options = [];
        for (const opt of filterOptions) {
            options.push(
                <input type="radio" id={opt.tag} name={spec.name} key={opt.tag}
                       value={opt.tag} checked={opt.tag === selected}
                       onChange={(event) => this.selectFilter(spec.name, event)}>
                </input>
            );
            options.push(<label htmlFor={opt.tag} key={"label " + opt.tag}>{opt.name || opt.tag}</label>)
        }
        return <div className="filter-block" key={spec.name}><strong>{spec.name}:</strong>{options}</div>;
    }

    private selectMetric(event: any) {
        this.setState({...this.state, metric: event.target.value});
    }

    private selectFilter(filter: string, event: any) {
        let filters = {...this.state.filters};
        filters[filter] = event.target.value === "none" ? null : event.target.value || null;
        const filterSet = new Set<string>(Object.values(filters).filter(val => val != null));
        this.setState({...this.state, filters: filters, filterSet: filterSet});
    }

    renderHelp() {
        return (
            <p className="description">
                Above each task group there is a panel which contains the following actions:
                <span className="circled-icon">+</span> expands the group into individual tasks,
                <span className="circled-icon">?</span> displays a tooltip providing additional information about the
                group,
                <span className="circled-icon">🔗</span> opens a link to the paper or the official website,
                <span className="circled-icon">✕</span> closes the group and removes it from the evaluation.
            </p>
        )
    }

    renderPaperInfo() {
        let el = [];
        if (this.state.data?.paper) {
            el.push(<a key="paper" href={this.state.data.paper} target="_blank">🔗 Paper</a>);
        }
        if (this.state.data?.citation) {
            el.push(
                <a key="cite" onClick={() => copyTextToClipboard(this.state.data?.citation)}
                   data-tooltip-id="table-tooltip" data-tooltip-content={"Citation copied to clipboard!"}>
                    🗍 Cite
                </a>
            );
        }
        return el.length == 0 ? null : <span className={"paper-info"}>{el}</span>;
    }

    renderChangelog() {
        const models = [];
        const limit = Math.min(this.state.changelog.length, 5);
        for (let i = 0; i < limit; i++) {
            const key = this.state.changelog[i].model;
            models.push(<span key={key}>{key} ({this.state.changelog[i].updated})</span>);
            models.push(i < (limit - 1) ? ", " : "");
        }
        return <p className="description"><strong>Recently added models: </strong>{models}</p>;
    }

    private renderVariantSelector(): ReactNode {
        const variants = this.state.variants;
        if (!variants || variants.length <= 1) return null;
        const options = [];
        for (let i = 0; i < variants.length; i++) {
            const variant = variants[i];
            const id = `variant-${i}`;
            options.push(
                <input type="radio" id={id} name="variant" key={id}
                       value={String(i)} checked={i === this.state.activeTab}
                       onChange={() => this.selectVariant(i)}>
                </input>
            );
            options.push(
                <label htmlFor={id} key={"label " + id} title={variant.lead}>{variant.tab}</label>
            );
        }
        return (
            <div className="filter-block" key="variant">
                <strong>Benchmark:</strong>
                {options}
            </div>
        );
    }

    render() {
        if (this.state.data == null) return null;
        const variantSelector = this.renderVariantSelector();
        const metric: string = this.state.metric || this.state.data.metrics[0].id;
        const desc = this.state.data.description ?
            <p className="description" dangerouslySetInnerHTML={{__html: this.state.data.description}}></p> : null;
        const help = this.state.data.options?.showHelp ? this.renderHelp() : null;
        const changelog = this.state.data.options?.showChangelog ? this.renderChangelog() : null;
        const categories = this.state.data.categories ?
            <p className="description" dangerouslySetInnerHTML={{__html: this.state.data.categories}}></p> : null;
        const grading = this.state.data.grading ?
            <p className="description" dangerouslySetInnerHTML={{__html: this.state.data.grading}}></p> : null;
        let footer = null;
        if (this.state.data.options?.showFooter) {
            footer =
                <div className="footer">Author: <a target="_blank" href="https://github.com/sdadas" rel="noreferrer">Sławomir
                    Dadas</a></div>;
        }
        return (
            <div className="root-layout">
                <h1 style={{"maxWidth": "1200px", "marginBottom": "20px", "width": "100%"}}>
                    {this.state.data.title}
                    {this.renderPaperInfo()}
                </h1>
                {desc}{help}{changelog}<br/>
                <div className="filters-panel">
                    {variantSelector}
                    {this.renderMetrics()}
                    {this.renderFilters()}
                </div>
                <TableView data={this.state.data} mainMetric={metric} tags={this.state.filterSet}/>
                <p className="description">
                    <h2>Categories</h2>
                    {categories}
                </p>
                <p className="description">
                    <h2>Grading</h2>
                    {grading}
                </p>
                <ExamplesView/>
                <Tooltip id="table-tooltip" openOnClick={true} variant="light"/>
                {footer}
            </div>
        )
    }
}
